/**
 * CapaDepthPanel (C-10) — structured root-cause taxonomy + the 30/90/180-day
 * effectiveness trend, for the CAPA detail screen.
 */
import { useState } from 'react';
import { apiFetch } from '../../api/client';
import styles from './CapaDepthPanel.module.css';

export interface EffectivenessCheck {
  checkId: string;
  intervalDays: number;
  outcome: 'EFFECTIVE' | 'PARTIALLY_EFFECTIVE' | 'NOT_EFFECTIVE';
  notes: string | null;
  performedAtUtc: string;
  performedByName: string | null;
}
const RC_CATEGORIES: { value: string; label: string }[] = [
  { value: 'HUMAN_ERROR', label: 'Human error' },
  { value: 'EQUIPMENT', label: 'Equipment' },
  { value: 'MATERIAL', label: 'Material' },
  { value: 'METHOD_PROCEDURE', label: 'Method / procedure' },
  { value: 'ENVIRONMENT', label: 'Environment' },
  { value: 'MEASUREMENT', label: 'Measurement' },
  { value: 'TRAINING', label: 'Training' },
  { value: 'SOFTWARE_SYSTEM', label: 'Software / system' },
  { value: 'OTHER', label: 'Other' },
];
const INTERVALS = [30, 90, 180];
const OUTCOME_LABEL: Record<string, string> = {
  EFFECTIVE: 'Effective', PARTIALLY_EFFECTIVE: 'Partially effective', NOT_EFFECTIVE: 'Not effective',
};
function rcLabel(v: string | null) { return RC_CATEGORIES.find((c) => c.value === v)?.label ?? '— not set —'; }
function outcomeClass(o: string) {
  return o === 'EFFECTIVE' ? styles.ocGood : o === 'PARTIALLY_EFFECTIVE' ? styles.ocWarn : styles.ocBad;
}

export function CapaDepthPanel({
  capaId, rootCauseCategory, rootCauseDetail, effectivenessChecks, onChanged,
}: {
  capaId: string;
  rootCauseCategory: string | null;
  rootCauseDetail: string | null;
  effectivenessChecks: EffectivenessCheck[];
  onChanged: () => void;
}) {
  const [editRc, setEditRc] = useState(false);
  const [cat, setCat] = useState(rootCauseCategory ?? 'HUMAN_ERROR');
  const [detail, setDetail] = useState(rootCauseDetail ?? '');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const [interval, setIntervalDays] = useState(30);
  const [outcome, setOutcome] = useState('EFFECTIVE');
  const [notes, setNotes] = useState('');

  const byInterval = new Map(effectivenessChecks.map((c) => [c.intervalDays, c]));

  async function saveRootCause() {
    setMsg(null); setBusy(true);
    try {
      await apiFetch(`/api/v1/capa/${encodeURIComponent(capaId)}/root-cause`, { method: 'POST', body: { category: cat, detail } });
      setEditRc(false); onChanged();
    } catch (e: unknown) { setMsg(e instanceof Error ? e.message : 'Could not save the root cause.'); }
    finally { setBusy(false); }
  }
  async function recordCheck() {
    setMsg(null); setBusy(true);
    try {
      await apiFetch(`/api/v1/capa/${encodeURIComponent(capaId)}/effectiveness-check`, { method: 'POST', body: { intervalDays: interval, outcome, notes } });
      setNotes(''); onChanged();
    } catch (e: unknown) { setMsg(e instanceof Error ? e.message : 'Could not record the check.'); }
    finally { setBusy(false); }
  }

  return (
    <>
      <section className={styles.panel} aria-labelledby="capa-rc">
        <header className={styles.head}>
          <h2 id="capa-rc" className={styles.title}>Root cause</h2>
          {!editRc && <button type="button" className={styles.action} onClick={() => { setCat(rootCauseCategory ?? 'HUMAN_ERROR'); setDetail(rootCauseDetail ?? ''); setEditRc(true); setMsg(null); }}>Edit</button>}
        </header>
        {msg && <div className={styles.err} role="alert">{msg}</div>}
        {!editRc ? (
          <>
            <span className={styles.rcPill}>{rcLabel(rootCauseCategory)}</span>
            <p className={styles.detail}>{rootCauseDetail || '— no detail recorded —'}</p>
          </>
        ) : (
          <div className={styles.form}>
            <label className={styles.field}><span>Category</span>
              <select value={cat} onChange={(e) => setCat(e.target.value)}>
                {RC_CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select></label>
            <label className={styles.field}><span>Detail</span>
              <textarea rows={2} value={detail} onChange={(e) => setDetail(e.target.value)} placeholder="Confirmed root cause…" /></label>
            <div className={styles.formActions}>
              <button type="button" className={styles.btnNeutral} onClick={() => setEditRc(false)}>Cancel</button>
              <button type="button" className={styles.btnPrimary} onClick={() => void saveRootCause()} disabled={busy}>Save root cause</button>
            </div>
          </div>
        )}
      </section>

      <section className={styles.panel} aria-labelledby="capa-eff">
        <header className={styles.head}>
          <h2 id="capa-eff" className={styles.title}>Effectiveness trend</h2>
        </header>
        <div className={styles.trend}>
          {INTERVALS.map((d) => {
            const c = byInterval.get(d);
            return (
              <div key={d} className={styles.trendCard}>
                <span className={styles.trendLabel}>{d}-day</span>
                {c ? (
                  <span className={`${styles.trendOutcome} ${outcomeClass(c.outcome)}`}>{OUTCOME_LABEL[c.outcome]}</span>
                ) : (
                  <span className={styles.trendPending}>Pending</span>
                )}
                {c?.notes && <span className={styles.trendNote}>{c.notes}</span>}
              </div>
            );
          })}
        </div>
        <div className={styles.recordRow}>
          <select value={interval} onChange={(e) => setIntervalDays(Number(e.target.value))} className={styles.select} aria-label="Interval">
            {INTERVALS.map((d) => <option key={d} value={d}>{d}-day</option>)}
          </select>
          <select value={outcome} onChange={(e) => setOutcome(e.target.value)} className={styles.select} aria-label="Outcome">
            <option value="EFFECTIVE">Effective</option>
            <option value="PARTIALLY_EFFECTIVE">Partially effective</option>
            <option value="NOT_EFFECTIVE">Not effective</option>
          </select>
          <input type="text" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notes (optional)" className={styles.textInput} />
          <button type="button" className={styles.btnPrimary} onClick={() => void recordCheck()} disabled={busy}>Record check</button>
        </div>
      </section>
    </>
  );
}
